/*
 * Copyright © HatioLab Inc. All rights reserved.
 */

import { Component, ScenePopup, Properties, ComponentNature, sceneComponent, RealObject, ThreeContainer } from '@hatiolab/things-scene'
import * as THREE from 'three'
import { Stock, type StockMaterialProvider } from './stock.js'

const NATURE: ComponentNature = {
  mutable: false,
  resizable: true,
  rotatable: true,
  properties: [
    {
      type: 'number',
      label: 'fov',
      name: 'fov',
      property: 'fov'
    },
    {
      type: 'number',
      label: 'near',
      name: 'near',
      property: 'near'
    },
    {
      type: 'number',
      label: 'far',
      name: 'far',
      property: 'far'
    },
    {
      type: 'number',
      label: 'zoom',
      name: 'zoom',
      property: 'zoom'
    },
    {
      type: 'number',
      label: 'camera-x',
      name: 'cameraX'
    },
    {
      type: 'number',
      label: 'camera-y',
      name: 'cameraY'
    },
    {
      type: 'number',
      label: 'camera-z',
      name: 'cameraZ'
    },
    {
      type: 'select',
      label: 'precision',
      name: 'precision',
      property: {
        options: [
          {
            display: 'High',
            value: 'highp'
          },
          {
            display: 'Medium',
            value: 'mediump'
          },
          {
            display: 'Low',
            value: 'lowp'
          }
        ]
      }
    },
    {
      type: 'checkbox',
      label: 'anti-alias',
      name: 'antialias',
      property: 'antialias'
    },
    {
      type: 'checkbox',
      label: 'auto-rotate',
      name: 'autoRotate',
      property: 'autoRotate'
    },
    {
      type: 'checkbox',
      label: '3dmode',
      name: 'threed',
      property: 'threed'
    },
    {
      type: 'checkbox',
      label: 'debug',
      name: 'debug',
      property: 'debug'
    },
    {
      type: 'string',
      label: 'location-field',
      name: 'locationField',
      placeholder: 'location'
    },
    {
      type: 'board-selector',
      label: 'popup-scene',
      name: 'popupScene'
    },
    {
      type: 'id-input',
      label: 'legend-target',
      name: 'legendTarget',
      property: {
        component: 'legend'
      }
    },
    {
      type: 'number',
      label: 'rotation-speed',
      name: 'rotationSpeed'
    },
    {
      type: 'checkbox',
      label: 'hide-empty-stock',
      name: 'hideEmptyStock'
    },
    {
      type: 'select',
      label: 'popup-position',
      name: 'popupPosition',
      property: {
        options: [
          { display: '', value: '' },
          { display: 'CENTER', value: 'center' },
          { display: 'LEFTTOP', value: 'left-top' },
          { display: 'RIGHTTOP', value: 'right-top' },
          { display: 'LEFTBOTTOM', value: 'left-bottom' },
          { display: 'RIGHTBOTTOM', value: 'right-bottom' }
        ]
      }
    }
  ],
  help: 'scene/component/visualizer'
}

const WEBGL_NO_SUPPORT_TEXT = 'WebGL no support'

@sceneComponent('visualizer')
export class Visualizer extends ThreeContainer implements StockMaterialProvider {
  _legendTarget?: Component
  _focused_stock?: Stock

  _objects: { [id: string]: RealObject } = {}

  _stock_materials: THREE.Material[] = []
  _default_material?: THREE.Material
  _empty_material?: THREE.Material

  get hideEmptyStock(): boolean {
    return !!this.getState('hideEmptyStock')
  }

  get legendTarget() {
    const { legendTarget } = this.state

    if (!this._legendTarget && legendTarget) {
      this._legendTarget = this.root.findById?.(legendTarget)
      this._legendTarget && this._legendTarget.on('change', this.onLegendTargetChanged, this)
    }

    return this._legendTarget
  }

  putObject(id: string, object: RealObject) {
    id && (this._objects[id] = object)
  }

  getObject(id: string): RealObject {
    return this._objects[id]
  }

  /* Container Overides .. */

  dispose() {
    this._legendTarget && this._legendTarget.off('change', this.onLegendTargetChanged, this)
    delete this._legendTarget

    super.dispose()
  }

  get nature(): ComponentNature {
    return NATURE
  }

  resetMaterials() {
    this._stock_materials.forEach(m => {
      if (m.dispose) m.dispose()
    })

    this._stock_materials = []
  }

  /* Event Handlers */

  onchange(after: Properties, before: Properties) {
    if (before.hasOwnProperty('legendTarget') || after.hasOwnProperty('legendTarget')) {
      this._legendTarget && this._legendTarget.off('change', this.onLegendTargetChanged, this)
      delete this._legendTarget
      this.resetMaterials()
    }

    super.onchange(after, before)
  }

  onchangeData(): void {
    if (typeof this.data !== 'object') {
      return
    }

    let data = this.data as any
    const locationField = this.getState('locationField') || 'location'

    if (data instanceof Array) {
      data = data.reduce((acc, value, i, arr) => {
        const location = value[locationField]

        if (!location) {
          return acc
        }

        if (acc[location]) {
          acc[location]['items'].push(value)
        } else {
          acc[location] = { items: [value] }
        }

        return acc
      }, {})
    }

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const d = data[key]
        const object = this.getObject(key)
        if (object) {
          ;(object as Stock).onchangeStockData(d)
        }
      }
    }
  }

  onLegendTargetChanged(after: { [key: string]: any }, before: { [key: string]: any }) {
    if (after.hasOwnProperty('status') && before.hasOwnProperty('status')) this.resetMaterials()
  }

  onmouseup(e: MouseEvent) {
    if (!this.controlsManager.isActive) return

    // v10: ThreeContainer 기본 mouseup 처리 (기즈모, 선택 등)
    super.onmouseup(e)

    // Visualizer 고유: Stock 클릭 시 팝업 표시
    const { popupScene } = this.state
    if (!popupScene) return

    const camera = this.cameraManager.activeCamera
    const scene3d = this.sceneManager.scene
    if (!camera || !scene3d) return

    const realObject = this.eventManager.handleClick(camera, scene3d, e)

    if (realObject && realObject instanceof Stock) {
      // 이전 focused 해제
      if (this._focused_stock && this._focused_stock !== realObject) {
        this._focused_stock._focused = false
      }
      // 클릭한 Stock에 focused 설정 → onBeforeRender에서 회전 애니메이션
      realObject._focused = true
      realObject._focusedAt = performance.now()
      this._focused_stock = realObject
      this.invalidate()

      realObject.onmouseup(e, this, (data: any) => {
        ScenePopup.show(this as any, popupScene, {
          data,
          modal: false,
          closable: true,
          output: false,
          location: this.state.popupPosition || 'right-top'
        } as any)
      })
    } else if (!realObject) {
      // 빈 공간 클릭 시 focused 해제
      if (this._focused_stock) {
        this._focused_stock._focused = false
        delete this._focused_stock
      }
      ScenePopup.hide(this.root as any)
    }
  }
}
